import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ConfirmButton } from "./ConfirmButton";

const STAGE_COLORS = { new: "#FF5C5C", qualified: "#F59E0B", scoped: "#38BDF8", active: "#4ADE80", complete: "#D6D6D6" };
const STAGE_HINTS = {
  new: "Just arrived — reply first", qualified: "Real, worth scoping", scoped: "Scope written and sent",
  active: "Work under way", complete: "Done and delivered",
};
const ADVANCE = {
  new: { to: "qualified", label: "Mark qualified" },
  qualified: { to: "scoped", label: "Scope it" },
  scoped: { to: "active", label: "Start the work" },
  active: { to: "complete", label: "Mark complete", confirm: true },
};

const EnquiryCard = ({ e, labels, onStage, onToggle, onScopeSave, onScopeSend, busyId }) => {
  const busy = busyId === e.id;
  const stage = e.status || "new";
  const adv = ADVANCE[stage];
  const [scopeDraft, setScopeDraft] = useState(null);
  const [scopeBusy, setScopeBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const btn = "w-full flex items-center justify-center gap-1.5 text-[15px] font-semibold rounded-lg px-2 py-2 disabled:opacity-40 transition-colors duration-150";
  return (
    <div className="rounded-xl border border-[#2E2E2E] bg-[#050505] p-4 space-y-2.5" data-testid="enquiry-card">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[15px] font-semibold text-white truncate">{e.name}</p>
        <span className="text-[15px] font-medium text-[#D6D6D6] shrink-0">{new Date(e.created_at).toLocaleDateString()}</span>
      </div>
      <p className="text-[15px] font-medium text-[#D6D6D6] truncate">{e.email}{e.phone ? ` · ${e.phone}` : ""}</p>
      {e.working_with && <p className="text-[15px] font-medium text-[#D6D6D6] line-clamp-3">{e.working_with}</p>}
      {e.audience_segment && (
        <p className="text-[15px] font-medium text-[#D6D6D6]" data-testid="enquiry-audience">
          Audience: <span className="text-white">{{ business: "Business", nonprofit: "Non-profit", campaign: "Campaign", enterprise: "Enterprise" }[e.audience_segment] || e.audience_segment}</span>
        </p>
      )}
      {(e.flagged_questions || []).length > 0 && (
        <p className="text-[15px] font-medium text-[#D6D6D6]" data-testid="enquiry-flags">
          Opened legal questions: <span className="text-white">{e.flagged_questions.join(" · ")}</span>
        </p>
      )}
      <p className="text-[15px] font-semibold" data-testid="enquiry-score"
        style={{ color: e.qualification_score >= 67 ? "#4ADE80" : e.qualification_score >= 34 ? "#F59E0B" : "#FF5C5C" }}>
        Qualification {e.qualification_score ?? 0} / 100
        {(e.qualification_missing || []).length > 0 && (
          <span className="text-[#D6D6D6] font-medium"> — missing {e.qualification_missing.join(", ")}</span>
        )}
      </p>
      {e.draft_scope_pending && (
        <p className="text-[15px] font-medium text-[#38BDF8]" data-testid="draft-scope-pending">Writing the draft scope...</p>
      )}
      {e.draft_scope && (
        <details className="rounded-lg border border-[#2E2E2E] bg-[#111111] px-3 py-2" data-testid="draft-scope">
          <summary className="text-[15px] font-semibold text-[#38BDF8] cursor-pointer">
            Draft scope{e.scope_sent_at ? " — sent" : ""}
          </summary>
          <div className="mt-2 space-y-2">
            <textarea
              value={scopeDraft ?? e.draft_scope}
              onChange={(ev) => setScopeDraft(ev.target.value)}
              rows={9}
              data-testid="scope-editor"
              className="w-full rounded-lg bg-[#050505] border border-[#2E2E2E] px-3 py-2.5 text-[15px] font-medium text-[#D6D6D6] leading-relaxed outline-none focus:border-[#38BDF8] resize-y"
            />
            <div className="flex flex-wrap gap-2">
              {scopeDraft !== null && scopeDraft !== e.draft_scope && (
                <button
                  onClick={async () => {
                    setScopeBusy(true);
                    const ok = await onScopeSave(e.id, scopeDraft);
                    if (ok) setScopeDraft(null);
                    setScopeBusy(false);
                  }}
                  disabled={scopeBusy || !scopeDraft.trim()}
                  data-testid="scope-save-button"
                  className="text-[15px] font-semibold rounded-lg border border-[#38BDF8] text-[#38BDF8] px-3 py-1.5 hover:bg-[#38BDF8] hover:text-black disabled:opacity-40 transition-colors duration-150">
                  {scopeBusy ? "Saving..." : "Save changes"}
                </button>
              )}
              <button
                onClick={async () => {
                  setSendBusy(true);
                  if (scopeDraft !== null && scopeDraft !== e.draft_scope) {
                    const ok = await onScopeSave(e.id, scopeDraft);
                    if (!ok) { setSendBusy(false); return; }
                    setScopeDraft(null);
                  }
                  await onScopeSend(e.id, e.name);
                  setSendBusy(false);
                }}
                disabled={sendBusy || (scopeDraft !== null && !scopeDraft.trim())}
                data-testid="scope-send-button"
                className="text-[15px] font-semibold rounded-lg bg-white text-black px-3 py-1.5 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
                {sendBusy ? "Sending..." : e.scope_sent_at ? `Send again to ${e.name.split(" ")[0]}` : `Email it to ${e.name.split(" ")[0]}`}
              </button>
            </div>
            {e.scope_sent_at && (
              <p className="text-[14px] font-medium text-[#4ADE80]" data-testid="scope-sent-line">
                Sent {new Date(e.scope_sent_at).toLocaleString()}
              </p>
            )}
          </div>
        </details>
      )}
      {e.onboarding && stage !== "complete" && (
        <div className="rounded-lg border border-[#2E2E2E] bg-[#111111] px-3 py-2 space-y-1.5" data-testid="onboarding-checklist">
          <p className="text-[15px] font-semibold text-[#4ADE80]">Getting started</p>
          {e.onboarding.map((item, i) => (
            <label key={item.item} className="flex items-start gap-2 text-[15px] font-medium cursor-pointer" data-testid="onboarding-item">
              <input type="checkbox" checked={item.done} onChange={() => onToggle(e.id, i, !item.done)}
                className="accent-[#DC2626] w-4 h-4 mt-0.5 shrink-0" />
              <span className={item.done ? "text-[#D6D6D6] line-through" : "text-[#D6D6D6]"}>{item.item}</span>
            </label>
          ))}
        </div>
      )}
      <div className="space-y-2 pt-1">
        {adv && !adv.confirm && (
          <button onClick={() => onStage(e.id, adv.to)} disabled={busy} data-testid="enquiry-advance-button"
            className={`${btn} border border-[#2E2E2E] text-white hover:bg-white hover:text-black`}>
            {adv.label}
          </button>
        )}
        {adv && adv.confirm && (
          <ConfirmButton onConfirm={() => onStage(e.id, adv.to)} disabled={busy} testId="enquiry-advance-button"
            confirmLabel="Sure? Mark it complete"
            className={`${btn} border border-[#2E2E2E] text-white hover:bg-white hover:text-black`}
            confirmClassName={`${btn} border border-[#4ADE80] text-[#4ADE80]`}>
            {adv.label}
          </ConfirmButton>
        )}
        <select value={stage} onChange={(ev) => ev.target.value !== stage && onStage(e.id, ev.target.value)} disabled={busy}
          data-testid="enquiry-move-select"
          className="w-full rounded-lg bg-[#111111] border border-[#2E2E2E] text-[15px] font-medium text-[#D6D6D6] px-2.5 py-2 outline-none">
          {Object.entries(labels).map(([k, v]) => <option key={k} value={k}>{stage === k ? v : `Move to ${v}`}</option>)}
        </select>
      </div>
    </div>
  );
};

export const PipelineView = ({ data, api, authHeaders, onChanged }) => {
  const [busyId, setBusyId] = useState(null);
  const [runBusy, setRunBusy] = useState(false);
  const enquiries = data?.enquiries || [];

  useEffect(() => {
    if (!enquiries.some((e) => e.draft_scope_pending)) return;
    const t = setTimeout(onChanged, 4000);
    return () => clearTimeout(t);
  }, [enquiries, onChanged]);

  if (!data) return <p className="text-[17px] font-medium text-[#D6D6D6]">Loading...</p>;
  const stages = data.stages || [];
  const labels = data.stage_labels || {};

  const setStage = async (id, stage) => {
    setBusyId(id);
    try {
      const res = await fetch(`${api}/admin/enquiries/${id}/stage`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ stage }),
      });
      if (!res.ok) throw new Error();
      toast.success(stage === "scoped" ? "Moved to Scoped — the draft scope is being written." : `Moved to ${labels[stage] || stage}.`);
      onChanged();
    } catch {
      toast.error("The move didn't go through.");
    }
    setBusyId(null);
  };

  const toggleItem = async (id, index, done) => {
    try {
      const res = await fetch(`${api}/admin/enquiries/${id}/onboarding`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ index, done }),
      });
      if (!res.ok) throw new Error();
      onChanged();
    } catch {
      toast.error("That didn't save.");
    }
  };

  const saveScope = async (id, text) => {
    try {
      const res = await fetch(`${api}/admin/enquiries/${id}/scope`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ draft_scope: text }),
      });
      if (!res.ok) throw new Error();
      toast.success("Scope saved.");
      onChanged();
      return true;
    } catch {
      toast.error("The scope didn't save.");
      return false;
    }
  };

  const sendScope = async (id, name) => {
    try {
      const res = await fetch(`${api}/admin/enquiries/${id}/scope/send`, {
        method: "POST", headers: authHeaders,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "The send didn't go through.");
      toast.success(d.email_live
        ? `Scope emailed to ${d.sent_to}.`
        : `Scope send recorded for ${d.sent_to} — email is mocked until the Resend key lands.`);
      onChanged();
      return true;
    } catch (err) {
      toast.error(err.message || "The send didn't go through.");
      return false;
    }
  };

  const runFollowups = async () => {
    setRunBusy(true);
    try {
      const res = await fetch(`${api}/admin/followups/run`, { method: "POST", headers: authHeaders });
      const d = await res.json();
      if (!res.ok) throw new Error();
      toast.success(d.sent?.length ? `${d.sent.length} follow-up(s) sent.` : `Checked ${d.checked} lead(s) — nothing due yet.`);
      onChanged();
    } catch {
      toast.error("The follow-up check didn't go through.");
    }
    setRunBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="pipeline-view">
      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#2E2E2E] bg-[#111111] p-4">
        <p className="text-[15px] font-medium text-[#D6D6D6] max-w-2xl">
          Anyone verified who never books gets exactly one follow-up the next day — never a sequence. Enquiries sitting in New for 48 hours put a reminder in your alerts.
        </p>
        <button onClick={runFollowups} disabled={runBusy} data-testid="run-followups-button"
          className="ml-auto flex items-center gap-2 text-[15px] font-semibold rounded-xl bg-white text-black px-4 py-2.5 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
          {runBusy ? "Checking..." : "Send due follow-ups"}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {stages.map((s) => {
          const items = enquiries.filter((e) => (e.status || "new") === s);
          return (
            <div key={s} className="rounded-2xl border border-[#2E2E2E] bg-[#111111] flex flex-col min-h-[140px]" data-testid={`pipeline-column-${s}`}>
              <div className="p-3 border-b border-[#2E2E2E] flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLORS[s] }} />
                <span className="text-[15px] font-bold" style={{ color: STAGE_COLORS[s] }}>{labels[s] || s}</span>
                <span className="text-[15px] font-medium text-[#D6D6D6] ml-auto" data-testid={`pipeline-count-${s}`}>{items.length}</span>
              </div>
              <p className="text-[15px] font-medium text-[#D6D6D6] px-3 pt-2">{STAGE_HINTS[s]}</p>
              <div className="p-3 space-y-3 flex-1">
                {items.map((e) => (
                  <EnquiryCard key={e.id} e={e} labels={labels} onStage={setStage} onToggle={toggleItem}
                    onScopeSave={saveScope} onScopeSend={sendScope} busyId={busyId} />
                ))}
                {items.length === 0 && <p className="text-[15px] font-medium text-[#D6D6D6] pt-2">Empty</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
